import axios from "axios";
import { getClaims } from "../../../modules/login";
import { themes } from "../../../modules/theme";
import {
  aggregateMealsByDay,
  decimalPlaces,
  getAverageGoals,
  getFinalKcalAndPercentage,
  roundMealValue,
} from "../../../helpers/mealsHelper";
import {
  dailyTargetIds,
  getMacronutrientColor,
  kcalMultiplier,
  UNIT,
} from "../../../modules/constants";
import i18n from "../../../i18n";
import { handleHttpErrors } from "../../../modules/http";

//Constants
const SET_GOALS = "SET_GOALS";
const LOGOUT = "LOGOUT";
const RESET_STATE = "RESET_STATE";

//Acessors
const getGoalsList = (state) => state?.goalsList;
const getGoalsStatus = (state) => state?.status;
const getCurrentDailyTargets = (state) => state?.currentDailyTargets;

const canViewTargetsHistory = (state) =>
  getClaims(state, "participant.target.history.canView");
const canCreateNewTarget = (state) =>
  getClaims(state, "participant.target.canCreate");

//Default state
const initialState = {
  goalsList: null,
  currentDailyTargets: null,
  status: null,
};

export const targetsReducer = (state = initialState, action) => {
  switch (action.type) {
    case SET_GOALS: {
      const { goals, currentDailyTargets } = action;
      return Object.assign({}, state, {
        status: true,
        goalsList: goals,
        currentDailyTargets: currentDailyTargets,
      });
    }
    case RESET_STATE:
      return initialState;
    case LOGOUT:
      return initialState;
    default:
      return state;
  }
};

const resetState = () => (dispatch) => {
  dispatch({ type: RESET_STATE });
};

const updateDailyTargets =
  (participantId, dailyTarget, reasonForChange) => (dispatch, getState) => {
    const body = {
      participantId: participantId,
      dailyTarget: dailyTarget,
      reasonForChange: reasonForChange,
    };

    return axios
      .put(`/api/diet/dailytarget`, body)
      .then(() => {
        return Promise.resolve("Daily target updated successfully!");
      })
      .catch((error) => {
        const defaultMessage = error.response?.data
          ? error.response.data
          : "There was an error while trying to update the daily target!";

        return handleHttpErrors(error.request?.status, {
          default: defaultMessage,
        });
      });
  };

const fetchGoals =
  (startDate, endDate, participantId = null, t) =>
  (dispatch, getState) => {
    const params = {
      StartDate: startDate,
      EndDate: endDate,
    };

    if (participantId) {
      params.ParticipantId = participantId;
    }

    return axios
      .get(`/api/diet/dailyentries/`, { params })
      .then(({ data }) => {
        dispatch({
          type: SET_GOALS,
          goals: parseGoalsData(data, t),
          currentDailyTargets: data?.dailyTargets,
        });
      })
      .catch((error) => {
        dispatch({ type: SET_GOALS, goals: null });

        return handleHttpErrors(error.request?.status, {
          default: i18n.t("errorText.dietDiary.dailyTargets.get"),
        });
      });
  };

const parseGoalsData = (data, t) => {
  const { dailyTargets, dailyEntries } = data;

  const formattedData = aggregateMealsByDay(
    dailyEntries.filter((item) => item.completed)
  );

  const kcalObj = getFinalKcalAndPercentage(formattedData);

  const averageGoals = getAverageGoals(kcalObj);

  return {
    CALORIES: {
      id: "calories",
      title: t("appText.dietDiary.targets.target"),
      origin: `${Number(roundMealValue(dailyTargets?.calories ?? 0)).toFixed(
        decimalPlaces
      )}kcal`,
      color: themes.default.geminiGray,
      label: t("appText.dietDiary.dailyAverage"),
      value: `${Number(roundMealValue(averageGoals.calories.value)).toFixed(
        decimalPlaces
      )}kcal`,
    },
    LONG_CHAIN_FATS: {
      id: "longChainFats",
      title: `${dailyTargets?.longChainFatsPercentage ?? 0}${
        UNIT.PERCENTAGE
      } ${t("appText.targets.fromLongChainFats")}`,
      color: getMacronutrientColor(
        dailyTargetIds.LONG_CHAIN_FATS_ATTR_ID,
        themes.default
      ),
      label: t("appText.dietDiary.dailyAverage"),
      value: `${Number(
        roundMealValue(averageGoals.longChainFats.value) * kcalMultiplier.lcf
      ).toFixed(decimalPlaces)}kcal`,
    },
    MEDIUM_CHAIN_FATS: {
      id: "mediumChainFats",
      title: `${dailyTargets?.mediumChainFatsPercentage ?? 0}${
        UNIT.PERCENTAGE
      } ${t("appText.targets.fromMediumChainFats")}`,
      color: getMacronutrientColor(
        dailyTargetIds.MEDIUM_CHAIN_FATS_ATTR_ID,
        themes.default
      ),
      label: t("appText.dietDiary.dailyAverage"),
      value: `${Number(
        roundMealValue(averageGoals.mediumChainFats.value) * kcalMultiplier.mcf
      ).toFixed(decimalPlaces)}kcal`,
    },
    PROTEIN: {
      id: "protein",
      title: `${dailyTargets?.proteinPercentage ?? 0}${UNIT.PERCENTAGE} ${t(
        "appText.targets.fromProtein"
      )}`,
      color: getMacronutrientColor(
        dailyTargetIds.PROTEIN_ATTR_ID,
        themes.default
      ),
      label: t("appText.dietDiary.dailyAverage"),
      value: `${Number(
        roundMealValue(averageGoals.protein.value) * kcalMultiplier.protein
      ).toFixed(decimalPlaces)}kcal`,
    },
    CARBOHYDRATES: {
      id: "carbohydrates",
      title: `${dailyTargets?.carbohydratesPercentage ?? 0}${
        UNIT.PERCENTAGE
      } ${t("appText.targets.fromCarbohydrates")}`,
      color: getMacronutrientColor(
        dailyTargetIds.CARBS_ATTR_ID,
        themes.default
      ),
      label: t("appText.dietDiary.dailyAverage"),
      value: `${Number(
        roundMealValue(averageGoals.carbohydrates.value) * kcalMultiplier.carbs
      ).toFixed(decimalPlaces)}kcal`,
    },
  };
};

export const mapStateToProps = (state) => {
  return {
    goalsList: getGoalsList(state),
    status: getGoalsStatus(state),
    currentDailyTargets: getCurrentDailyTargets(state),
    canViewTargetsHistory: canViewTargetsHistory(state),
    canCreateNewTarget: canCreateNewTarget(state),
  };
};

export const mapDispatchToProps = {
  fetchGoals,
  resetState,
  updateDailyTargets,
};
